import type Vehicle from '@/classes/vehicle_classes/vehicle'

const kg_hydrogen_equivalent_kWh = 33.3

/**
 * Checks if a vehicle is a hydrogen vehicle.
 *
 * Note: This function checks the current fuel type of the vehicle, regardless of its base model.
 * If the vehicle has been modified to use a different fuel type, this function will return the correct result.
 * The function first checks the vehicle's fuel type, and then falls back to the fuel type of the vehicle's EV model if the former is not present.
 *
 * @param vehicle - The vehicle to check
 * @returns True if the vehicle is a hydrogen vehicle, false otherwise
 */
export function isHydrogen(vehicle: Vehicle): boolean {
  if (vehicle.fuelType) return vehicle.fuelType === 'Hydrogen'
  return vehicle.evModel?.fuelType === 'HYDROGEN'
}

/**
 * Converts a given energy in kilowatt-hours (kWh) to the equivalent mass of hydrogen in kilograms (kg).
 *
 * @param kWh - The energy in kilowatt-hours.
 * @returns The equivalent mass of hydrogen in kilograms.
 */

export function kWhToHydrogenKgs(kWh: number): number {
  return kWh / kg_hydrogen_equivalent_kWh
}

/**
 * Converts a given mass of hydrogen in kilograms (kg) to the equivalent energy in kilowatt-hours (kWh).
 *
 * @param hydrogenKgs - The mass of hydrogen in kilograms.
 * @returns The equivalent energy in kilowatt-hours.
 */

function hydrogenKgsToKWh(hydrogenKgs: number): number {
  return hydrogenKgs * kg_hydrogen_equivalent_kWh
}

/**
 * Converts a given battery size in kWh to the equivalent hydrogen tank size in kg.
 *
 * @param batterySize - The battery size in kWh.
 * @returns The equivalent hydrogen tank size in kg.
 * @throws If the battery size is negative.
 */
export function batterySizeToTankSize(batterySize: number): number {
  if (batterySize < 0) {
    throw new Error('Battery size cannot be negative')
  }
  return kWhToHydrogenKgs(batterySize)
}

/**
 * Converts a given hydrogen tank size in kg to the equivalent battery size in kWh.
 *
 * @param tankSize - The hydrogen tank size in kg.
 * @returns The equivalent battery size in kWh.
 * @throws If the hydrogen tank size is negative.
 */
export function tankSizeToBatterySize(tankSize: number): number {
  if (tankSize < 0) {
    throw new Error('Hydrogen tank size cannot be negative')
  }
  return hydrogenKgsToKWh(tankSize)
}

/**
 * Converts a given charging rate in kW to the equivalent hydrogen refuelling speed in kg/hour.
 *
 * @param chargingRate - The charging rate in kW.
 * @returns The equivalent hydrogen refuelling speed in kg/hour.
 */
export function chargingRateToHydrogenRefuellingSpeed(chargingRate: number): number {
  if (chargingRate < 0) {
    throw new Error('Charging rate cannot be negative')
  }
  return chargingRate / kg_hydrogen_equivalent_kWh
}

/**
 * Converts a given hydrogen refuelling speed in kg/hour to the equivalent charging rate in kW.
 *
 * @param hydrogenRefuellingSpeed - The hydrogen refuelling speed in kg/hour.
 * @returns The equivalent charging rate in kW.
 */
export function hydrogenRefuellingSpeedToChargingRate(hydrogenRefuellingSpeed: number): number {
  if (hydrogenRefuellingSpeed < 0) {
    throw new Error('Hydrogen refuelling speed cannot be negative')
  }
  return hydrogenRefuellingSpeed * kg_hydrogen_equivalent_kWh
}

/**
 * Calculates the time in hours to refuel a hydrogen vehicle from its current fuel level to a target fuel level.
 *
 * @param {{ hydrogenRefuellingSpeed: number, hydrogenTankSize: number, targetFuelLevel: number, currentFuelLevel: number }} options - An object containing the following properties:
 * @param {number} options.hydrogenRefuellingSpeed - Hydrogen refuelling speed in kg/hour.
 * @param {number} options.hydrogenTankSize - Total size of the hydrogen tank in kg.
 * @param {number} options.targetFuelLevel - target fuel level as a decimal representing a percentage between 0 and 1, e.g. 0.8 = 80%.
 * @param {number} options.currentFuelLevel - current fuel level as a decimal representing a percentage between 0 and 1, e.g. 0.8 = 80%.
 * @returns The time in hours to refuel the vehicle from its current fuel level to the target fuel level.
 */
export function calculateHydrogenRefuellingTime({
  hydrogenRefuellingSpeed,
  hydrogenTankSize,
  targetFuelLevel,
  currentFuelLevel,
}: {
  /** Hydrogen refuelling speed in kg/hour. */
  hydrogenRefuellingSpeed: number
  /** Total size of the hydrogen tank in kg. */
  hydrogenTankSize: number
  /** target fuel level as a decimal representing a percentage between 0 and 1, e.g. 0.8 = 80%. */
  targetFuelLevel: number
  /** current fuel level as a decimal representing a percentage between 0 and 1, e.g. 0.8 = 80%. */
  currentFuelLevel: number
}): number {
  // guard clauses
  if (hydrogenRefuellingSpeed <= 0) {
    throw new Error('Hydrogen refuelling speed must be greater than 0')
  }
  if (hydrogenTankSize <= 0) {
    throw new Error('Hydrogen tank size must be greater than 0')
  }
  if (targetFuelLevel < 0 || targetFuelLevel > 1) {
    throw new Error('Target fuel level must be between 0 and 1')
  }
  if (currentFuelLevel < 0 || currentFuelLevel > 1) {
    throw new Error('Current fuel level must be between 0 and 1')
  }

  const currentHydrogenKg = hydrogenTankSize * currentFuelLevel
  const targetHydrogenKg = hydrogenTankSize * targetFuelLevel

  if (currentHydrogenKg >= targetHydrogenKg) return 0

  return (targetHydrogenKg - currentHydrogenKg) / hydrogenRefuellingSpeed
}
